import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { propsSenzaPermesso, usePermessi } from "@/shared/auth/permessi";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import {
  aggiornaMagazzino,
  comeData,
  fetchScheda,
  oggiIso,
  prenotaMagazzino,
  RicaricaFallita,
  STATI_ATTIVI,
  type Candidato,
  type Impostazioni,
  type MagazzinoRiepilogo,
  type MisuraManuale,
  type OrdineCercato,
  type Prenotazione,
  type RitiroMancato,
  type SchedaMagazzino as Scheda,
} from "../api";
import { type AutomaticoPatch, AutomaticoRiga } from "./AutomaticoRiga";
import { BannerMancato } from "./BannerMancato";
import { CercaOrdine } from "./CercaOrdine";
import { Cronologia } from "./Cronologia";
import { ProssimoRitiro } from "./ProssimoRitiro";
import { RecapitiDialog } from "./RecapitiDialog";
import { RiprenotaDialog } from "./RiprenotaDialog";

type Props = { slot: string; onCambiata: () => void };

const CHIP =
  "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11.5px] font-semibold leading-tight";
/** Blocchi che si sciolgono dalla pagina dell'ordine, col suo «Invia LDV». */
const BLOCCHI_LDV = new Set(["manca la LDV"]);

const ESITO_CLS: Record<string, string> = {
  prenotato: "bg-green-soft",
  rifiutato: "bg-red-soft",
  esito_ignoto: "bg-amber-soft",
};

/** Ordine aggiunto a mano dalla ricerca libera, con le misure scritte se il PIM
 *  non le ha. Vive solo nella scheda finché non parte un ritiro. */
type Libero = { ordine: OrdineCercato; misura: MisuraManuale | null };

/** Il libero come riga della lista: misure a mano al posto di quelle del PIM. */
function comeCandidato({ ordine, misura }: Libero): Candidato {
  return misura
    ? { ...ordine, colli: misura.colli, peso_kg: misura.peso_kg, blocco: null }
    : ordine;
}

/** Esito dell'ultima prenotazione; `riprenota` = nato dal dialog «Riprenota». */
type Esito = { p: Prenotazione; riprenota: boolean };

function testoEsito({ p, riprenota }: Esito): string {
  const giorno = comeData(p.data_ritiro).toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "numeric",
  });
  if (p.stato === "prenotato") return `Prenotato per ${giorno} · codice ${p.codice}`;
  if (p.stato === "rifiutato") {
    const motivo = p.errore ?? "motivo non indicato";
    // La riprenota ha già liberato gli ordini sul server: il testo lo dice.
    return riprenota
      ? `Gli ordini sono di nuovo prenotabili, ma SDA ha rifiutato il ritiro: ${motivo}. Riprova dalla scheda.`
      : `SDA ha rifiutato: ${motivo}. Correggi e riprova.`;
  }
  return "SDA non ha risposto in tempo: controlla sul portale Bracchi prima di riprovare.";
}

/** La scadenza di spedizione in un chip rosso: il ritiro va prenotato adesso. */
function scadenza(deadline: string | null, oggi: string): string | null {
  if (!deadline) return null;
  const quando = new Date(deadline).getTime();
  const mezzanotte = (fraGiorni: number) => {
    const d = new Date(`${oggi}T00:00:00`);
    d.setDate(d.getDate() + fraGiorni);
    return d.getTime();
  };
  if (quando < mezzanotte(0)) return "scaduta";
  if (quando < mezzanotte(1)) return "scade oggi";
  return quando < mezzanotte(2) ? "scade domani" : null;
}

function orari(imp: Impostazioni): string {
  const fasce: [string, string][] = [
    [imp.apertura_mattina_da, imp.apertura_mattina_a],
    [imp.apertura_pomeriggio_da, imp.apertura_pomeriggio_a],
  ];
  const aperte = fasce.filter(([da, a]) => da && a).map(([da, a]) => `${da}–${a}`);
  return aperte.length > 0 ? `aperti ${aperte.join(" · ")}` : "";
}

export function SchedaMagazzino({ slot, onCambiata }: Props) {
  const [scheda, setScheda] = useState<Scheda | null>(null);
  const [scelti, setScelti] = useState<Set<number>>(new Set());
  const [giorno, setGiorno] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<Esito | null>(null);
  const [recapiti, setRecapiti] = useState(false);
  const [daRiprenotare, setDaRiprenotare] = useState<RitiroMancato | null>(null);
  const [liberi, setLiberi] = useState<Map<number, Libero>>(new Map());
  // Letto da `carica` (useCallback su [slot]): un ricaricamento non deve
  // togliere la spunta a un ordine aggiunto a mano, che i candidati non hanno.
  const idLiberi = useRef<Set<number>>(new Set());
  idLiberi.current = new Set(liberi.keys());
  const { puoScrivere, inCaricamento } = usePermessi();
  const puo = puoScrivere("spedizioni");
  const oggi = oggiIso();
  // Il primo caricamento (e solo lui, salvo un `azzeraSelezione` esplicito)
  // riempie la selezione dai «pronto»: un ricaricamento qualsiasi (switch
  // automatico, azione in cronologia, orario) NON deve rispuntare un ordine
  // che l'operatore ha appena tolto a mano.
  const primoCarico = useRef(true);
  // Id PRONTI all'ultimo caricamento riuscito — non «candidati mai visti
  // prima»: un ordine che ERA bloccato e diventa pronto (LDV arrivata nel
  // frattempo) deve spuntarsi da solo, non solo un candidato nuovo di zecca.
  const idPrecedentementePronti = useRef<Set<number>>(new Set());

  const carica = useCallback(
    async (opts?: {
      azzeraSelezione?: boolean;
      mantieniErrore?: boolean;
    }): Promise<Scheda | null> => {
      if (!opts?.mantieniErrore) setErrore(null);
      try {
        const s = await fetchScheda(slot);
        setScheda(s);
        const azzera = opts?.azzeraSelezione ?? primoCarico.current;
        const idNuovi = new Set(s.candidati.map((c) => c.order_id));
        const idBloccati = new Set(s.candidati.filter((c) => c.blocco).map((c) => c.order_id));
        const idPronti = new Set(s.candidati.filter((c) => c.pronto).map((c) => c.order_id));
        // Letto PRIMA di setScelti: l'updater passato a setScelti può essere
        // chiamato da React DOPO questa funzione (batching, con un altro
        // setState già in coda) — un const locale non risente della
        // riscrittura del ref qualche riga sotto, un `.current` letto DENTRO
        // l'updater sì. Bug osservato su React 18/createRoot: un ricarico che
        // portava un pronto nuovo non lo spuntava mai, perché l'updater
        // vedeva già il ref riscritto con gli id nuovi.
        const idPrimaPronti = idPrecedentementePronti.current;
        setScelti((precedenti) =>
          azzera
            ? idPronti
            : new Set([
                // Resta spuntato solo chi era spuntato, esiste ancora ED è
                // ancora selezionabile: un ordine appena bloccato esce da
                // solo, o ogni prenotazione con la sua casella già spuntata
                // fallirebbe.
                ...[...precedenti].filter(
                  (id) => idLiberi.current.has(id) || (idNuovi.has(id) && !idBloccati.has(id)),
                ),
                // Si aggiunge chi è pronto ADESSO e non lo era all'ultimo
                // giro (bloccato o candidato nuovo, non importa): si spunta
                // da solo una volta sola, non ad ogni ricaricamento.
                ...[...idPronti].filter((id) => !idPrimaPronti.has(id)),
              ]),
        );
        primoCarico.current = false;
        idPrecedentementePronti.current = idPronti;
        setGiorno((g) => (g && s.giorni.includes(g) ? g : (s.giorni[0] ?? null)));
        return s;
      } catch (e) {
        // Con `mantieniErrore` il chiamante ha già scritto il SUO messaggio
        // (es. una prenotazione fallita): un fallimento anche qui non deve
        // sovrascriverlo con quello, meno utile, del semplice ricaricamento.
        if (!opts?.mantieniErrore) setErrore(prettifyApiError(e));
        return null;
      }
    },
    [slot],
  );

  useEffect(() => {
    void carica();
  }, [carica]);

  const giaPrenotato = useMemo(
    () =>
      scheda?.prenotazioni.find(
        (p) => p.data_ritiro === giorno && !p.aggiuntiva && STATI_ATTIVI.includes(p.stato),
      ) ?? null,
    [scheda, giorno],
  );

  if (!scheda) {
    return (
      <section className="rounded-2xl border border-line bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)] p-5 text-ink">
        {errore ? (
          <>
            <ErrorBanner message={errore} className="mb-2" />
            <button
              type="button"
              onClick={() => void carica()}
              className="rounded-full border border-line-2 px-3 py-1.5 text-[12.5px] font-bold"
            >
              Riprova
            </button>
          </>
        ) : (
          <p className="text-[12.5px]">Caricamento…</p>
        )}
      </section>
    );
  }
  const r = scheda.riepilogo;
  // Un candidato bloccato (es. «manca la LDV») si può aggiungere a mano: da
  // lì in poi la sua riga è quella del ritiro libero, senza blocco.
  const righeLibere = [...liberi.values()];
  const righe: (Candidato & { avvisi?: string[] })[] = [
    ...righeLibere.map((l) => ({ ...comeCandidato(l), avvisi: l.ordine.avvisi })),
    ...scheda.candidati.filter((c) => !liberi.has(c.order_id)),
  ];
  const selezionati = righe.filter((c) => scelti.has(c.order_id));
  const liberiScelti = righeLibere.filter((l) => scelti.has(l.ordine.order_id));
  // Il motivo di permesso si scrive SOLO a permessi caricati (stesso guard di
  // RitiriPage): mentre `/me` è in volo `puo` è già `false` (i tasti restano
  // spenti, prudente), ma «non hai i permessi» potrebbe rivelarsi falso un
  // istante dopo. Stringa vuota = tasto spento, ma senza motivo annunciato.
  const bloccato = !puo
    ? inCaricamento
      ? ""
      : "Non hai i permessi dell'area spedizioni: puoi consultare i ritiri, non prenotarli."
    : r.mancanti.includes("indirizzo")
      ? "Indirizzo del magazzino da completare: «Modifica recapiti»."
      : r.mancanti.includes("telefono")
        ? "Manca il telefono del magazzino: «Modifica recapiti»."
        : null;

  async function prenota() {
    if (!giorno) return;
    if (
      giaPrenotato &&
      !window.confirm(
        `Per questo giorno c'è già il ritiro ${giaPrenotato.codice ?? "da verificare"}: ne prenoto un secondo?`,
      )
    )
      return;
    setBusy(true);
    setErrore(null);
    // Un tentativo nuovo azzera anche l'esito del precedente: senza, un
    // banner verde «Prenotato… codice X» resterebbe accanto a un errore
    // rosso appena arrivato — sembrerebbero riferirsi allo stesso tentativo.
    setEsito(null);
    try {
      const p = await prenotaMagazzino(slot, {
        data: giorno,
        order_ids: selezionati.map((c) => c.order_id),
        aggiuntiva: giaPrenotato !== null,
        ...(liberiScelti.length > 0 && {
          libero: true,
          misure: liberiScelti.flatMap((l) => (l.misura ? [l.misura] : [])),
        }),
      });
      await dopoPrenotazione(p);
    } catch (e) {
      // Il backend chiede di ricaricare la pagina su un fallimento: lo
      // facciamo qui, ma SENZA perdere il messaggio appena scritto — la
      // ricarica azzererebbe l'errore se non le dicessimo di tenerlo.
      setErrore(prettifyApiError(e));
      await carica({ mantieniErrore: true });
    } finally {
      setBusy(false);
    }
  }

  /** Esito in vista e scheda ricaricata: dopo «Prenota» e dopo «Riprenota».
   *  La Riprenota non tocca i candidati: azzerare rispunterebbe chi
   *  l'operatore ha tolto a mano (e i liberati pronti si spuntano da soli). */
  async function dopoPrenotazione(p: Prenotazione, riprenota = false) {
    setEsito({ p, riprenota });
    // Gli aggiunti a mano sono partiti: la scheda torna ai soli candidati. Su
    // un rifiuto restano, da rispuntare e riprovare.
    if (!riprenota && p.stato === "prenotato") setLiberi(new Map());
    await carica({ azzeraSelezione: !riprenota });
    onCambiata();
  }

  /** Riprenota fallita: si rilegge, perché il server può aver già liberato gli
   *  ordini. Se il ritiro non è più tra i mancati, riprovare dal dialog darebbe
   *  solo «non risulta mancato»: il dialog si chiude e il messaggio passa alla
   *  scheda, dove gli ordini liberati sono di nuovo tra i candidati. */
  async function riprenotaFallita(id: number, messaggio: string) {
    const s = await carica({ mantieniErrore: true });
    onCambiata();
    if (s && !s.mancati.some((m) => m.prenotazione_id === id)) {
      setDaRiprenotare(null);
      setEsito(null);
      setErrore(messaggio);
    }
  }

  /** Passato ad ``AutomaticoRiga``: rilancia dopo aver mostrato l'errore, così
   *  la riga sa se salvare è andato a buon fine (torna al valore salvato,
   *  ferma il busy) senza doverlo indovinare dal banner della pagina. */
  async function automatico(patch: AutomaticoPatch) {
    setErrore(null);
    let riepilogo: MagazzinoRiepilogo;
    try {
      riepilogo = await aggiornaMagazzino(slot, patch);
    } catch (e) {
      setErrore(prettifyApiError(e));
      throw e;
    }
    // Il PATCH ha già il valore nuovo: lo scriviamo subito nello stato
    // locale, così il campo mostra l'orario appena salvato anche se il
    // reload sotto fallisce — niente più bisogno che il GET separato porti
    // lo stesso valore per riflettersi in UI.
    setScheda((s) => (s ? { ...s, riepilogo } : s));
    const ricaricata = await carica();
    onCambiata();
    if (!ricaricata) {
      // Il PATCH è andato a segno (il server ha il valore nuovo, e lo
      // riflettiamo già sopra) ma non siamo riusciti a rileggere il resto
      // della scheda: carica() ha già scritto il SUO errore nel banner della
      // pagina. RicaricaFallita dice al chiamante di non trattarlo come un
      // salvataggio fallito (niente revert del campo).
      throw new RicaricaFallita(
        "Salvato, ma la scheda non si è aggiornata del tutto: ricarica la pagina.",
      );
    }
  }

  const ind = scheda.indirizzo;
  const telefono = r.impostazioni.telefono || ind?.phone;
  const aperto = orari(r.impostazioni);
  return (
    <section className="rounded-2xl border border-line bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)] text-ink">
      <header className="flex items-end justify-between gap-4 border-b border-line px-5 py-4">
        <div>
          <h2 className="font-display text-[22px] font-bold">{r.etichetta}</h2>
          <p className="text-[12px]">
            {ind
              ? `${ind.address} · ${ind.postcode} ${ind.city} (${ind.state_iso})`
              : "Indirizzo da completare"}
            {telefono && (
              <>
                {" "}
                · tel. <span className="tabular-nums">{telefono}</span>
              </>
            )}
            {aperto && ` · ${aperto}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRecapiti(true)}
          {...propsSenzaPermesso(puo, "spedizioni")}
          className="text-[12px] font-bold text-ink underline disabled:cursor-not-allowed"
        >
          Modifica recapiti
        </button>
      </header>
      <ErrorBanner message={errore} className="mx-5 mt-3" />
      {esito && (
        <p
          role="status"
          className={`mx-5 mt-3 rounded-sm px-3 py-2 text-[12.5px] font-semibold ${ESITO_CLS[esito.p.stato] ?? "bg-paper-2"}`}
        >
          {testoEsito(esito)}
        </p>
      )}
      {scheda.mancati.map((m) => (
        <BannerMancato
          key={m.prenotazione_id}
          mancato={m}
          onRiprenota={setDaRiprenotare}
          puo={puo}
        />
      ))}
      <div className="grid grid-cols-[minmax(0,1fr)_330px] max-lg:grid-cols-1">
        <div>
          <div className="px-5 py-4">
            <h3 className="mb-2 flex items-center justify-between text-[12.5px] font-extrabold">
              Pronti al ritiro
              {r.segnale_pronto && (
                <span className="text-[12px] font-semibold">{r.segnale_pronto}</span>
              )}
            </h3>
            {righe.length === 0 ? (
              <p className="text-[12.5px]">
                {slot === "itamoby"
                  ? "Nessun ordine pronto: quando arriva la richiesta LDV compare qui."
                  : "Nessun ordine con LDV SDA da ritirare."}
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {righe.map((c) => {
                  const chipScadenza = scadenza(c.ship_deadline, oggi);
                  return (
                    <li
                      key={c.order_id}
                      className={`flex flex-wrap items-center gap-3 py-2 ${c.blocco ? "bg-red-soft/40" : ""}`}
                    >
                      <input
                        type="checkbox"
                        aria-label={`${c.rif_fornitore || c.reference} ${c.cliente}`}
                        checked={scelti.has(c.order_id)}
                        disabled={Boolean(c.blocco) || busy || !puo}
                        onChange={(e) =>
                          setScelti((s) => {
                            const n = new Set(s);
                            if (e.target.checked) n.add(c.order_id);
                            else n.delete(c.order_id);
                            return n;
                          })
                        }
                        className="h-4 w-4 accent-ink"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block tabular-nums text-[12px]">
                          {c.rif_fornitore || c.reference}
                        </span>
                        <span className="block truncate text-[12px]">
                          {c.cliente} · {c.canale}
                        </span>
                      </span>
                      {c.ldv && <span className="tabular-nums text-[12px]">LDV {c.ldv}</span>}
                      <span className="text-[12px]">
                        <b>
                          {c.colli} {c.colli === 1 ? "collo" : "colli"}
                        </b>{" "}
                        · {c.peso_kg.toLocaleString("it-IT")} kg
                      </span>
                      {chipScadenza && (
                        <span className={`${CHIP} bg-red-soft`}>{chipScadenza}</span>
                      )}
                      {c.blocco ? (
                        <span className={`${CHIP} bg-red-soft`}>{c.blocco}</span>
                      ) : c.richiesta_il ? (
                        <span className={`${CHIP} bg-paper-2`}>
                          richiesta{" "}
                          {new Date(c.richiesta_il).toLocaleString("it-IT", {
                            weekday: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      ) : null}
                      {c.avvisi && (
                        <>
                          <span className={`${CHIP} bg-amber-soft`}>aggiunto a mano</span>
                          {c.avvisi.map((a) => (
                            <span key={a} className={`${CHIP} bg-amber-soft`}>
                              {a}
                            </span>
                          ))}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setLiberi((m) => {
                                const n = new Map(m);
                                n.delete(c.order_id);
                                return n;
                              });
                              setScelti((sc) => {
                                const n = new Set(sc);
                                n.delete(c.order_id);
                                return n;
                              });
                            }}
                            className="text-[12px] font-bold text-ink underline disabled:cursor-not-allowed"
                          >
                            Togli
                          </button>
                        </>
                      )}
                      {c.blocco && BLOCCHI_LDV.has(c.blocco) && (
                        <Link
                          to={`/ordini/${encodeURIComponent(c.reference)}`}
                          className="text-[12px] font-bold text-ink underline"
                        >
                          Invia LDV
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <CercaOrdine
            slot={slot}
            presenti={new Set(righe.filter((c) => !c.blocco).map((c) => c.order_id))}
            disabilitato={busy || !puo}
            onAggiungi={(ordine, misura) => {
              setLiberi((m) => new Map(m).set(ordine.order_id, { ordine, misura }));
              setScelti((sc) => new Set(sc).add(ordine.order_id));
            }}
          />
          <AutomaticoRiga
            possibile={r.automatico_possibile}
            automatico={r.impostazioni.automatico}
            ora={r.impostazioni.ora_automatico}
            oraMattina={r.impostazioni.ora_automatico_mattina}
            limiteInGiornata={scheda.limite_in_giornata}
            puo={puo}
            onCambia={automatico}
          />
          <div className="border-t border-line px-5 py-4">
            <h3 className="mb-2 text-[12.5px] font-extrabold">Cronologia</h3>
            <Cronologia
              filtro={{ magazzino: slot }}
              iniziali={scheda.prenotazioni}
              onCambiata={async () => {
                // L'esito mostrato sopra puo' riferirsi a un ritiro appena
                // annullato/risolto da qui sotto: senza azzerarlo resterebbe
                // un banner vecchio sopra una cronologia già cambiata.
                setEsito(null);
                const ricaricata = await carica();
                onCambiata();
                if (!ricaricata) {
                  // L'azione (annulla/risolvi esito) È andata a buon fine:
                  // solo il reload qui sotto è fallito. RicaricaFallita dice
                  // a Cronologia di sbloccare comunque la riga, non di
                  // lasciarla su «…» in attesa di dati che non arrivano.
                  throw new RicaricaFallita(
                    "Salvato, ma la scheda non si è aggiornata: ricarica la pagina.",
                  );
                }
              }}
            />
          </div>
        </div>
        <ProssimoRitiro
          selezionati={selezionati}
          giorni={scheda.giorni}
          oggi={oggi}
          giorno={giorno}
          onGiorno={setGiorno}
          giaPrenotato={giaPrenotato ? (giaPrenotato.codice ?? "da verificare") : null}
          busy={busy}
          bloccato={bloccato}
          limite={scheda.limite_in_giornata}
          onPrenota={() => void prenota()}
        />
      </div>
      {recapiti && (
        <RecapitiDialog
          riepilogo={r}
          onChiudi={() => setRecapiti(false)}
          onSalvato={() => {
            setRecapiti(false);
            void carica();
            onCambiata();
          }}
        />
      )}
      {daRiprenotare && (
        <RiprenotaDialog
          mancato={daRiprenotare}
          giorni={scheda.giorni}
          giorniOccupati={scheda.prenotazioni
            .filter((p) => STATI_ATTIVI.includes(p.stato))
            .map((p) => p.data_ritiro)}
          onChiudi={() => {
            setDaRiprenotare(null);
            // Rete di sicurezza: se la rilettura dopo un errore è fallita, la
            // scheda può avere ancora il vecchio avviso su ordini già liberi.
            void carica();
            onCambiata();
          }}
          onFatto={(p) => {
            setDaRiprenotare(null);
            void dopoPrenotazione(p, true);
          }}
          onErrore={(messaggio) => riprenotaFallita(daRiprenotare.prenotazione_id, messaggio)}
        />
      )}
    </section>
  );
}
